// Change #100463 - Back End > Company Details > Subscriptions (Super Admin),
// and with readOnly the customer's own Billing > Subscriptions (/settings/4/9):
// the same screen (Paul: settings must match the back end, which is where
// CRMit manages on the customer's behalf), with no buttons, no tolerance and
// the customer's own endpoints.
//
// Everything this company is subscribed to, in one place for CRMit:
//  - Billing: Standard or Enterprise, with the switch. For Enterprise, the
//    plan: which master product it is based on, users included, the secret
//    allow-up-to, seats in use and the bespoke agreed price, editable.
//  - The subscription products it pays for (user licences and bolt-ons such
//    as SIM plans or WhatsApp): quantity, price, period, total, next renewal,
//    and Add subscription. Nothing per person here: who holds what is on the
//    Users tab (Paul).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Col, Row, Table } from "reactstrap";
import { toast } from "react-toastify";

import InfoCard from "../../../../Components/Common/InfoCard/InfoCard";
import AppModal from "../../../../Components/Common/AppModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import EnterprisePlanFields, {
  enterprisePlanPayload,
  PRICE_PERIODS,
  validateEnterprisePlan,
} from "../../../../Components/Common/EnterprisePlanFields";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import RenewalWhen from "../../../../Components/Common/RenewalWhen";
import AddSubscriptionModal from "./AddSubscriptionModal";
import useCompanyLicences from "./useCompanyLicences";
import EditProductModal, { cardLabel } from "./EditProductModal";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";
import BillingModeModal from "../../CompanyReport/list/BillingModeModal";
import {
  enterprisePlanAdminCompanyApi,
  enterprisePlanAdminUpdateApi,
  subscriptionLicenceBillingApi,
} from "../../../../helpers/fakebackend_helper";

const periodLabel = (p) => PRICE_PERIODS.find((x) => x.value === p)?.label || "per month";

export default function CompanySubscriptions({ companyId, businessName, readOnly = false }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [ready, setReady] = useState(false);
  const [priceReady, setPriceReady] = useState(false);
  // Change #100463 - saved cards and whether delta 229 (card + pause) ran.
  const [cards, setCards] = useState([]);
  const [billingReady, setBillingReady] = useState(false);
  const [pausing, setPausing] = useState("");
  const [error, setError] = useState("");
  const [switching, setSwitching] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const { licences, loading: licLoading, error: licError, reload: reloadLicences } = useCompanyLicences(companyId, "active", readOnly);

  const load = useCallback(async () => {
    setLoading(true);
    if (readOnly) {
      try {
        const [b, c] = await Promise.all([
          subscriptionLicenceBillingApi().catch(() => null),
          FetchAPI("/masterpaymentcard/company/list", "GET").catch(() => null),
        ]);
        setData(b?.status === 200 ? b.subscriptionbilling || { Mode: "STANDARD" } : { Mode: "STANDARD" });
        setReady(true);
        setCards(c?.masterpaymentcard_list || []);
        setError("");
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const res = await enterprisePlanAdminCompanyApi(companyId);
      if (res?.status === 200) {
        setData(res.enterpriseplan_data || null);
        setProducts(res.subscriptionproduct_list || []);
        setReady(Boolean(res.ready));
        setPriceReady(Boolean(res.price_ready));
        setCards(res.card_list || []);
        setBillingReady(Boolean(res.licence_billing_ready));
        setError("");
      } else {
        setError(res?.message || "Could not load the billing for this company");
      }
    } catch (e) {
      setError("Could not load the billing for this company");
    } finally {
      setLoading(false);
    }
  }, [companyId, readOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const enterprise = data?.Mode === "ENTERPRISE";
  const baseName = products.find((p) => String(p.Id) === String(data?.BaseProduct_Id))?.Name;

  const openEdit = () =>
    setEditing({
      PlanName: data?.PlanName || "",
      MaxUsers: data?.MaxUsers ?? "",
      MaxUsersTolerance: data?.MaxUsersTolerance ?? "",
      BaseProduct_Id: data?.BaseProduct_Id ? String(data.BaseProduct_Id) : "",
      PriceLocalSubTotal: data?.PriceLocalSubTotal ?? "",
      PricePeriod: data?.PricePeriod || "MONTHLY",
      RrpLocalSubTotal: data?.RrpLocalSubTotal ?? "",
      // Change #100463 - a plan with no fee line yet asks when billing starts
      // and the first charge date (never defaulted, so nobody is charged by
      // surprise).
      needsFeeDates: billingReady && !data?.Fee,
      FeeStartDate: "",
      FeeRenewalDate: "",
    });

  const saveEdit = async () => {
    const problem = validateEnterprisePlan(editing);
    if (problem) {
      toast.error(problem);
      return;
    }
    setSaving(true);
    try {
      const body = { Company_Id: Number(companyId), ...enterprisePlanPayload(editing) };
      if (!priceReady) {
        delete body.PriceLocalSubTotal;
        delete body.PricePeriod;
      }
      const res = await enterprisePlanAdminUpdateApi([body]);
      if (res?.status === 200) {
        if (res.message && res.message !== "Updated Successfully") toast.warning(res.message);
        else toast.success("Plan updated");
        setEditing(null);
        load();
        reloadLicences();
      } else {
        toast.error(res?.message || "Could not save the plan");
      }
    } catch (e) {
      toast.error("Could not save the plan");
    } finally {
      setSaving(false);
    }
  };

  // Live licences grouped by product. Enterprise seats are one row (the
  // plan: users included and the agreed price), not one per seat.
  const planId = enterprise && data?.SubscriptionProduct_Id ? String(data.SubscriptionProduct_Id) : "";
  const products_ = useMemo(() => {
    const map = {};
    licences.forEach((l) => {
      const pid = String(l.SubscriptionProduct_Id);
      if (pid === planId) return;
      // Paul: no row for the "Unlicenced" placeholder product (#101).
      if (pid === "101" || /^unlicen[cs]ed$/i.test(String(l.SubscriptionProduct_NamePrinted || "").trim())) return;
      const eachKey = (parseFloat(l.AmountLocalSubTotal) || 0).toFixed(2);
      const card = l.BillingMasterPaymentCard_Id ? String(l.BillingMasterPaymentCard_Id) : "";
      const endKey = l.EndDateTime ? String(l.EndDateTime).slice(0, 10) : "";
      const k = [pid, eachKey, l.SubscriptionLicenceDurationType_Id || "", card, Number(l.FlagBillingPaused) === 1 ? "p" : "", endKey].join("|");
      const m = (map[k] = map[k] || {
        id: k,
        productId: pid,
        each: eachKey,
        card,
        end: l.EndDateTime || null,
        name: l.SubscriptionProduct_NamePrinted || `Product #${k}`,
        qty: 0,
        licences: [],
        total: 0,
        prices: new Set(),
        cards: new Set(),
        paused: 0,
        start: null,
        periods: new Set(),
        next: null,
      });
      m.qty += 1;
      m.licences.push(l);
      m.cards.add(l.BillingMasterPaymentCard_Id ? String(l.BillingMasterPaymentCard_Id) : "");
      if (Number(l.FlagBillingPaused) === 1) {
        m.paused += 1;
        // Change #100463 - when it was paused (earliest in the row).
        if (l.BillingPausedDateTime && (!m.pausedAt || String(l.BillingPausedDateTime) < m.pausedAt)) m.pausedAt = String(l.BillingPausedDateTime);
      }
      if (l.StartDateTime && (!m.start || String(l.StartDateTime) < m.start)) m.start = String(l.StartDateTime);
      const each = parseFloat(l.AmountLocalSubTotal) || 0;
      m.total += each;
      m.prices.add(each.toFixed(2));
      if (l.SubscriptionLicenceDurationType_Name) m.periods.add(l.SubscriptionLicenceDurationType_Name);
      if (l.RenewalDateTime && String(l.RenewalDateTime) < "9999" && (!m.next || String(l.RenewalDateTime) < m.next)) {
        m.next = String(l.RenewalDateTime);
      }
    });
    return Object.values(map).sort(
      (a, b) => a.name.localeCompare(b.name) || Number(b.each) - Number(a.each) || a.card.localeCompare(b.card)
    );
  }, [licences, planId]);

  const cardText = (id) =>
    id ? cardLabel(cards.find((c) => String(c.Id) === String(id))) || `Card #${id}` : "Primary card";

  // Change #100463 - the Enterprise plan fee line (billed like any
  // subscription) as a row object for the Billing / Pause / Resume buttons.
  const fee = enterprise ? data?.Fee || null : null;
  const feeProduct = useMemo(
    () =>
      fee
        ? {
            id: "plan-fee",
            name: data?.PlanName || "Enterprise plan",
            qty: 1,
            licences: [{ ...fee }],
            prices: new Set([(parseFloat(fee.AmountLocalSubTotal) || 0).toFixed(2)]),
            paused: Number(fee.FlagBillingPaused) === 1 ? 1 : 0,
            isFee: true,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fee, data?.PlanName]
  );
  const planDiscount =
    data?.RrpLocalSubTotal != null && data?.PriceLocalSubTotal != null && Number(data.RrpLocalSubTotal) > Number(data.PriceLocalSubTotal)
      ? (Number(data.RrpLocalSubTotal) - Number(data.PriceLocalSubTotal)).toFixed(2)
      : null;
  // RRP of a product row = the product's list price (Super Admin view only).
  const rrpOf = (p) => {
    const prod = products.find((x) => String(x.Id) === String(p.productId));
    return prod && prod.AmountLocalSubTotal != null ? Number(prod.AmountLocalSubTotal).toFixed(2) : null;
  };

  // Change #100463 - Cancel: end every licence in the row on a date (default
  // today). Billed up to then, not renewed after it, stops giving access.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelDate, setCancelDate] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const openCancel = (p) => {
    setCancelDate(new Date().toISOString().slice(0, 10));
    setCancelTarget(p);
  };
  const saveCancel = async () => {
    if (!cancelTarget || !cancelDate) {
      toast.error("Pick the end date");
      return;
    }
    setCancelling(true);
    try {
      const res = await FetchAPI(
        "/subscriptionlicenceadmin/update",
        "PUT",
        cancelTarget.licences.map((l) => ({ Id: l.Id, EndDateTime: `${cancelDate} 23:59:59`, FlagConfirmBackdatedRenewal: 1 }))
      );
      if (Number(res?.status) === 200) {
        toast.success(`${cancelTarget.name} cancelled from ${cancelDate.split("-").reverse().join("/")}`);
        if (cancelTarget.isFee) load();
        setCancelTarget(null);
        reloadLicences();
      } else {
        toast.error(res?.message || "Could not cancel it");
      }
    } catch (e) {
      toast.error("Could not cancel it");
    } finally {
      setCancelling(false);
    }
  };

  // Pause: the nightly card run skips these licences until resumed.
  const pauseProduct = async (p) => {
    setPausing(p.id);
    const pad = (n) => String(n).padStart(2, "0");
    const d = new Date();
    const at = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    try {
      const res = await FetchAPI(
        "/subscriptionlicenceadmin/update",
        "PUT",
        p.licences.map((l) => ({ Id: l.Id, FlagBillingPaused: 1, BillingPausedDateTime: at, FlagConfirmBackdatedRenewal: 1 }))
      );
      if (Number(res?.status) === 200) {
        toast.success(`${p.name} paused. The card run will not charge it until you resume.`);
        reloadLicences();
        if (p.isFee) load();
      } else {
        toast.error(res?.message || "Could not pause it");
      }
    } catch (e) {
      toast.error("Could not pause it");
    } finally {
      setPausing("");
    }
  };

  return (
    <Row className="g-3">
      <Col lg={12}>
        <InfoCard
          title="Subscriptions"
          icon="ri-stack-line"
          accent="indigo"
          actions={
            readOnly ? null : (
            <div className="d-flex gap-2">
              {!loading && ready && (
                <button
                  type="button"
                  className="btn btn-sm btn-soft-secondary"
                  onClick={() =>
                    setSwitching({ Id: Number(companyId), BusinessName: businessName, mode: data?.Mode || "STANDARD" })
                  }
                >
                  {enterprise ? "Switch to Standard" : "Switch to Enterprise"}
                </button>
              )}
              <button type="button" className="btn btn-sm btn-primary" onClick={() => setAdding(true)} disabled={!products.length}>
                <i className="ri-add-line me-1"></i>Add subscription
              </button>
            </div>
            )
          }
        >
          {loading || licLoading ? (
            <LoadingSpinner />
          ) : error || licError ? (
            <div className="alert alert-warning mb-0">{error || licError}</div>
          ) : (
            <>
              {!ready && !readOnly && (
                <div className="alert alert-warning fs-13">
                  Enterprise billing needs delta 225 and the backend upload.
                </div>
              )}
              {ready && !enterprise && (
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Badge color="light" className="text-body fs-12">
                    Standard
                  </Badge>
                  <span className="text-muted fs-13">Buys licences by card.</span>
                </div>
              )}
              {products_.length === 0 && !enterprise ? (
                <div className="text-muted">No subscriptions yet.</div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-middle table-nowrap mb-0">
                    <thead className="text-muted">
                      <tr>
                        <th className="text-end">Qty</th>
                        <th>Subscription</th>
                        <th>Card</th>
                        <th className="text-end">RRP</th>
                        <th className="text-end">Discount</th>
                        <th className="text-end">Price each</th>
                        <th>Billed</th>
                        <th className="text-end">Total (ex VAT)</th>
                        <th>Starts</th>
                        <th>Next renewal</th>
                        <th>Ends</th>
                        {!readOnly && <th></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {enterprise && (
                        <tr className="table-light">
                          <td className="text-end fw-semibold">{data?.PlanReady ? `${data.MaxUsers ?? "-"} users` : "-"}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <Badge color="primary">Enterprise</Badge>
                              <span className="fw-semibold">{data?.PlanReady ? data.PlanName : "Plan not set up"}</span>
                              {Number(fee?.FlagBillingPaused) === 1 && (
                                <Badge color="" className="badge-soft-warning">
                                  Paused
                                </Badge>
                              )}
                            </div>
                            {Number(fee?.FlagBillingPaused) === 1 && fee?.BillingPausedDateTime && (
                              <div className="text-muted fs-12">Paused on {HandleDateTime(fee.BillingPausedDateTime)}</div>
                            )}
                            {data?.PlanReady && (
                              <div className="text-muted fs-12 mt-1">
                                {baseName ? `Based on ${baseName} · ` : ""}
                                <span className={data.OverCap || data.AtLimit ? "text-danger fw-semibold" : ""}>
                                  {data.Used ?? 0} in use
                                </span>
                                {!readOnly && data.Limit != null && data.Limit !== data.MaxUsers ? ` · allow up to ${data.Limit} (secret)` : ""}
                              </div>
                            )}
                          </td>
                          <td className={fee?.BillingMasterPaymentCard_Id ? "" : "text-muted"}>{fee ? cardText(fee.BillingMasterPaymentCard_Id) : "-"}</td>
                          <td className="text-end">{data?.RrpLocalSubTotal != null ? `£${Number(data.RrpLocalSubTotal).toFixed(2)}` : <span className="text-muted">-</span>}</td>
                          <td className="text-end">{planDiscount != null ? `£${planDiscount}` : <span className="text-muted">-</span>}</td>
                          <td className="text-end text-muted">-</td>
                          <td>{data?.PriceLocalSubTotal != null ? periodLabel(data.PricePeriod).replace("per ", "Per ") : "-"}</td>
                          <td className="text-end fw-semibold">
                            {data?.PriceLocalSubTotal != null ? (
                              `£${Number(data.PriceLocalSubTotal).toFixed(2)}`
                            ) : (
                              <span className="text-muted fw-normal">Not set</span>
                            )}
                          </td>
                          <td>{fee?.StartDateTime ? HandleDateTime(fee.StartDateTime) : <span className="text-muted">-</span>}</td>
                          <td>
                            {fee ? (
                              <RenewalWhen at={fee.RenewalDateTime} paused={Number(fee.FlagBillingPaused) === 1} />
                            ) : (
                              <span className="text-muted">Not billed yet</span>
                            )}
                          </td>
                          <td>{fee?.EndDateTime ? HandleDateTime(fee.EndDateTime) : <span className="text-muted">-</span>}</td>
                          {!readOnly && (
                          <td className="text-end">
                            <div className="hstack gap-2 justify-content-end">
                              <button type="button" className="btn btn-sm btn-soft-primary" onClick={openEdit} title="Users, RRP, discount and price">
                                <i className="ri-pencil-line me-1"></i>
                                {data?.PlanReady ? "Edit" : "Set up"}
                              </button>
                              {feeProduct && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-info"
                                  title="Start date, next renewal, card, pause and end"
                                  onClick={() => setEditProduct({ ...feeProduct, resume: false })}
                                >
                                  <i className="ri-calendar-line me-1"></i>Billing
                                </button>
                              )}
                              {feeProduct &&
                                (feeProduct.paused > 0 ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-soft-success"
                                    disabled={!billingReady}
                                    onClick={() => setEditProduct({ ...feeProduct, resume: true })}
                                  >
                                    <i className="ri-play-line me-1"></i>Resume
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-soft-warning"
                                    disabled={!billingReady || pausing === feeProduct.id}
                                    title="Stop the card run charging the plan"
                                    onClick={() => pauseProduct(feeProduct)}
                                  >
                                    <i className="ri-pause-line me-1"></i>Pause
                                  </button>
                                ))}
                              {feeProduct && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-danger"
                                  title="End the plan fee on a date"
                                  onClick={() => openCancel(feeProduct)}
                                >
                                  <i className="ri-close-circle-line me-1"></i>Cancel
                                </button>
                              )}
                            </div>
                          </td>
                          )}
                        </tr>
                      )}
                      {products_.map((p) => (
                        <tr key={p.id}>
                          <td className="text-end fw-semibold">{p.qty}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-semibold">{p.name}</span>
                              {p.paused > 0 && (
                                <Badge color="" className="badge-soft-warning">
                                  Paused
                                </Badge>
                              )}
                            </div>
                            {p.paused > 0 && p.pausedAt && (
                              <div className="text-muted fs-12">Paused on {HandleDateTime(p.pausedAt)}</div>
                            )}
                          </td>
                          <td className={p.card ? "" : "text-muted"}>{cardText(p.card)}</td>
                          <td className="text-end">{rrpOf(p) != null ? `£${rrpOf(p)}` : <span className="text-muted">-</span>}</td>
                          <td className="text-end">
                            {rrpOf(p) != null && Number(rrpOf(p)) > Number(p.each) ? (
                              `£${(Number(rrpOf(p)) - Number(p.each)).toFixed(2)}`
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-end">
                            £{p.each}
                          </td>
                          <td>{[...p.periods].join(", ") || "-"}</td>
                          <td className="text-end fw-semibold">£{p.total.toFixed(2)}</td>
                          <td>{p.start ? HandleDateTime(p.start) : "-"}</td>
                          <td>{p.next ? <RenewalWhen at={p.next} paused={p.paused > 0} /> : "-"}</td>
                          <td>{p.end ? HandleDateTime(p.end) : <span className="text-muted">-</span>}</td>
                          {!readOnly && (
                          <td className="text-end">
                            <div className="hstack gap-2 justify-content-end">
                              <button type="button" className="btn btn-sm btn-soft-primary" onClick={() => setEditProduct({ ...p, resume: false })}>
                                <i className="ri-pencil-line me-1"></i>Edit
                              </button>
                              {/* Always shown; disabled until the server has the
                                  pause column (delta 229 + latest backend). */}
                              {p.paused > 0 ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-success"
                                  disabled={!billingReady}
                                  title={billingReady ? "Restart billing" : "Needs the latest backend upload"}
                                  onClick={() => setEditProduct({ ...p, resume: true })}
                                >
                                  <i className="ri-play-line me-1"></i>Resume
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-warning"
                                  disabled={!billingReady || pausing === p.id}
                                  title={billingReady ? "Stop the card run charging this" : "Needs the latest backend upload"}
                                  onClick={() => pauseProduct(p)}
                                >
                                  <i className="ri-pause-line me-1"></i>Pause
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-soft-danger"
                                title="End these licences on a date"
                                onClick={() => openCancel(p)}
                              >
                                <i className="ri-close-circle-line me-1"></i>Cancel
                              </button>
                            </div>
                          </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
              <div className="text-muted fs-12 mt-2">
                {readOnly
                  ? "Who has each licence is on the Users screen. To change a subscription, contact CRMit."
                  : "Who holds which licence, and editing a single licence, is on the Licences tab."}
              </div>
            </>
          )}
        </InfoCard>
      </Col>

      <AppModal
        open={!!cancelTarget}
        onClose={() => !cancelling && setCancelTarget(null)}
        title={`Cancel ${cancelTarget?.name || "subscription"}`}
        subtitle={cancelTarget ? `${businessName || ""} · ${cancelTarget.licences.length} licence${cancelTarget.licences.length === 1 ? "" : "s"}` : ""}
        icon="ri-close-circle-line"
        headerAccent="indigo"
        size="sm"
        onSubmit={saveCancel}
        submitLabel="Cancel subscription"
        submitDisabled={cancelling}
        loading={cancelling}
        loadingText="Cancelling..."
      >
        <div>
          <label className="form-label mb-1">End date</label>
          <input
            type="date"
            className="form-control"
            value={cancelDate}
            onChange={(e) => setCancelDate(e.target.value)}
          />
          <div className="text-muted fs-12 mt-2">
            {cancelTarget?.isFee
              ? "The plan fee stops after this day: no more renewals are charged. The users' seats are not changed."
              : "These licences stop after this day: no more renewals are charged and they stop giving access. Anyone holding one loses it then."}
          </div>
        </div>
      </AppModal>

      <EditProductModal
        product={editProduct}
        resume={!!editProduct?.resume}
        hidePrice={!!editProduct?.isFee}
        cards={cards}
        billingReady={billingReady}
        businessName={businessName}
        onClose={() => setEditProduct(null)}
        onDone={() => {
          if (editProduct?.isFee) load();
          setEditProduct(null);
          reloadLicences();
        }}
      />

      <AddSubscriptionModal
        open={adding}
        companyId={companyId}
        businessName={businessName}
        products={products}
        cards={cards}
        billingReady={billingReady}
        onClose={() => setAdding(false)}
        onDone={() => {
          setAdding(false);
          reloadLicences();
        }}
      />

      <BillingModeModal
        company={switching}
        onClose={() => setSwitching(null)}
        onDone={() => {
          setSwitching(null);
          load();
          reloadLicences();
        }}
      />

      <AppModal
        open={Boolean(editing)}
        onClose={() => !saving && setEditing(null)}
        title="Enterprise plan"
        subtitle={businessName}
        icon="ri-shield-star-line"
        headerAccent="indigo"
        size="md"
        onSubmit={saveEdit}
        submitLabel="Save"
        submitDisabled={saving}
        loading={saving}
        loadingText="Saving..."
      >
        {editing && (
          <EnterprisePlanFields
            value={editing}
            onChange={setEditing}
            products={products}
            businessName={businessName}
            priceReady={priceReady}
          />
        )}
      </AppModal>
    </Row>
  );
}
